import { IcbtCommunication } from "api/types";
import { isPast, isThisWeek, isToday } from "date-fns";
import { useMemo } from "react";
import { filter, map, mapWithFeedback, partition, pipe, sortBy } from "remeda";

export const categories = ["today", "week", "all"] as const;
export type Categories = (typeof categories)[number];

type BasePatientData = { userId: number };

export type IcbtCommunicationWithPatientData<
  PatientData extends BasePatientData,
> = IcbtCommunication & {
  patient?: PatientData;
};

/**
 * Takes list of IcbtCommunication and returns a Map of categorized IcbtCommunication that have no feedback, but has feedback date.
 * If feedback date is ignored, it is filtered.
 *
 * ***today:*** has feedbacks that are overdue and due today.
 *
 * ***week:*** has today + feedbacks that are due this week.
 *
 * ***all:*** has all feedbacks that have no feedback but has feedback date
 *
 */

export const useCategorizedTodoCommunication = <
  PatientData extends BasePatientData,
>({
  icbtCommunication,
  patientsToIncludeInCommunication: patients,
  includeOnlyWithResponses = true,
  ignoreFeedbackDateFeatureEnabled,
}: {
  icbtCommunication?: IcbtCommunication[];
  patientsToIncludeInCommunication?: PatientData[];
  includeOnlyWithResponses?: boolean;
  ignoreFeedbackDateFeatureEnabled: boolean;
}) =>
  useMemo(() => {
    if (!icbtCommunication)
      return new Map(
        Object.entries({
          today: [],
          week: [],
          all: [],
        }),
      );
    return pipe(
      icbtCommunication,
      // Filter has a feedback_date and no feedbacks written by the psychologist, and has a response from the patient
      filter(
        (request) =>
          Boolean(request.feedback_date) &&
          (includeOnlyWithResponses ? request.responses.length > 0 : true) &&
          // No feedback should be published if "todo"
          !request.feedbacks.some(
            (feedback) => feedback.feedback_status === "published",
          ),
      ),
      // do not mark those that have ignored feedback date as "todo"
      filter((request) =>
        ignoreFeedbackDateFeatureEnabled
          ? request.feedback_date_ignored_at === null
          : true,
      ),
      // Include patient data
      map((request) => {
        return {
          ...request,
          patient: patients?.find(
            (p) => p.userId === Number(request.patient_user_id),
          ),
        };
      }),
      sortBy((request) => new Date(request.feedback_date)),
      // Partition to today and rest
      partition(
        (request) =>
          isToday(new Date(request.feedback_date)) ||
          isPast(new Date(request.feedback_date)),
      ),
      // partition the rest to week and final rest
      ([today, rest]) => [
        today,
        ...partition(rest, (r) =>
          isThisWeek(new Date(r.feedback_date), {
            weekStartsOn: 1, // Monday
          }),
        ),
      ],
      // include 'today' to 'week' and 'week' to 'all'
      mapWithFeedback((prev, curr) => {
        return [...prev, ...curr];
      }, [] as IcbtCommunication[]),
      // Give the categories names.
      ([today, week, all]) =>
        new Map<Categories, IcbtCommunicationWithPatientData<PatientData>[]>(
          Object.entries({
            today,
            week,
            all,
          } as const) as [
            Categories,
            IcbtCommunicationWithPatientData<PatientData>[],
          ][],
        ),
    );
  }, [
    icbtCommunication,
    includeOnlyWithResponses,
    patients,
    ignoreFeedbackDateFeatureEnabled,
  ]);
